import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  createPerformAutoScroll,
  scrollButtonIntoView
} from '../desktop/TopDock.utils'

export const useTopDockAutoScroll = (
  scrollableContentRef: React.RefObject<HTMLDivElement>
) => {
  const autoScrollAnimationFrameRef = useRef<number | null>(null)
  const isMouseOverScrollAreaRef = useRef(false)
  const currentMouseXRef = useRef<number | null>(null)

  const performAutoScroll = useMemo(
    () =>
      createPerformAutoScroll(
        scrollableContentRef,
        isMouseOverScrollAreaRef,
        currentMouseXRef,
        autoScrollAnimationFrameRef
      ),
    [scrollableContentRef]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const scrollContainer = scrollableContentRef.current
      if (!scrollContainer) return

      const containerRect = scrollContainer.getBoundingClientRect()
      const { clientX, clientY } = e

      if (
        clientX >= containerRect.left &&
        clientX <= containerRect.right &&
        clientY >= containerRect.top &&
        clientY <= containerRect.bottom
      ) {
        currentMouseXRef.current = clientX

        if (autoScrollAnimationFrameRef.current === null) {
          performAutoScroll()
        }
      }
    },
    [performAutoScroll, scrollableContentRef]
  )

  const handleMouseEnter = useCallback(() => {
    isMouseOverScrollAreaRef.current = true
  }, [])

  const handleMouseLeave = useCallback(() => {
    isMouseOverScrollAreaRef.current = false
    currentMouseXRef.current = null
    if (autoScrollAnimationFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollAnimationFrameRef.current)
      autoScrollAnimationFrameRef.current = null
    }
  }, [])

  useEffect(() => {
    const scrollContainer = scrollableContentRef.current
    if (!scrollContainer) return

    scrollContainer.addEventListener('mousemove', handleMouseMove)
    scrollContainer.addEventListener('mouseenter', handleMouseEnter)
    scrollContainer.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      scrollContainer.removeEventListener('mousemove', handleMouseMove)
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter)
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave)
      if (autoScrollAnimationFrameRef.current !== null) {
        cancelAnimationFrame(autoScrollAnimationFrameRef.current)
      }
    }
  }, [
    handleMouseMove,
    handleMouseEnter,
    handleMouseLeave,
    scrollableContentRef
  ])

  return {
    scrollButtonIntoView: (buttonElement: HTMLElement | null) => {
      scrollButtonIntoView(buttonElement, scrollableContentRef.current)
    }
  }
}
